/* eslint-disable react-hooks/exhaustive-deps */
import { useEffect, useState } from "react";
import dayjs from "dayjs";
import { withProtectedRoute } from "../../utils/HOC/withProtectedRoute";
import { PrimaryButton, SecondaryButton, SearchInput } from "../../components";
import { useMaterialFeedback } from "../../store/useMaterialFeedback";

function MaterialFeedbackScreen() {
  /** Hooks */
  const { feedback, hasNextPage, getFeedback, clearFeedback } =
    useMaterialFeedback();
  /** State */
  const [page, setPage] = useState(1);
  const [userNameInput, setUserNameInput] = useState("");
  const [activeUserName, setActiveUserName] = useState("");

  /** Effects */
  useEffect(() => {
    getFeedback(1, 20);
    return () => {
      clearFeedback();
    };
  }, []);

  /** Callbacks */
  const applyFilter = () => {
    const trimmed = userNameInput.trim();
    setActiveUserName(trimmed);
    setPage(1);
    getFeedback(1, 20, {
      userName: trimmed || undefined,
    });
  };

  const clearFilter = () => {
    setUserNameInput("");
    setActiveUserName("");
    setPage(1);
    getFeedback(1, 20);
  };

  const loadMore = () => {
    const nextPage = page + 1;
    setPage(nextPage);
    getFeedback(nextPage, 20, {
      userName: activeUserName || undefined,
    });
  };

  return (
    <div className="">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <h1 className="font-satoshi text-2xl font-bold text-dark">
          Material Feedback
        </h1>
        <div className="flex flex-col sm:flex-row gap-2 w-full sm:w-auto items-stretch sm:items-center">
          <SearchInput
            placeholder="Filter by User Name"
            value={userNameInput}
            onChange={(e) => setUserNameInput(e.target.value)}
            inputClassName="bg-white"
          />
          <div className="flex flex-col sm:flex-row gap-2">
            <PrimaryButton
              label="Apply"
              className="h-[44px] w-full sm:w-auto"
              onClick={applyFilter}
            />
            <SecondaryButton
              label="Clear"
              className="h-[44px] w-full sm:w-auto"
              onClick={clearFilter}
            />
          </div>
        </div>
      </div>

      <div className="w-full px-4 sm:px-7 py-7 mt-6 bg-white border border-gray-border drop-shadow-md">
        {!feedback?.length ? (
          <p className="text-center font-satoshi text-base text-dark">
            No material feedback entries yet.
          </p>
        ) : (
          <div className="relative overflow-x-auto">
            <table className="min-w-full w-full text-sm sm:text-base font-normal font-satoshi text-left text-dark">
              <thead className="font-medium bg-gray-50">
                <tr>
                  <th className="px-2 sm:px-4 py-2">Date</th>
                  <th className="px-2 sm:px-4 py-2">User</th>
                  <th className="px-2 sm:px-4 py-2">Drop ID</th>
                  <th className="px-2 sm:px-4 py-2">Selected Material</th>
                  <th className="px-2 sm:px-4 py-2">User Response</th>
                  <th className="px-2 sm:px-4 py-2">AI Keywords</th>
                </tr>
              </thead>
              <tbody>
                {feedback.map((item) => {
                  const dropId =
                    (item?.preRegisteredDrop as any)?._id ||
                    item?.preRegisteredDrop;
                  return (
                    <tr
                      key={item?._id}
                      className="bg-white border-b hover:bg-gray-50"
                    >
                      <td className="px-2 sm:px-4 py-3 whitespace-nowrap text-xs sm:text-sm">
                        {item?.createdAt
                          ? dayjs(item.createdAt).format("DD/MM/YY HH:mm")
                          : "-"}
                      </td>
                      <td className="px-2 sm:px-4 py-3">
                        <div className="flex flex-col">
                          <span className="font-medium">
                            {item?.userName || "Unknown user"}
                          </span>
                          <span className="text-xs text-gray-500 truncate">
                            {item?.user || "N/A"}
                          </span>
                        </div>
                      </td>
                      <td className="px-2 sm:px-4 py-3 text-primary-base">
                        {dropId ? `#${dropId}` : "N/A"}
                      </td>
                      <td className="px-2 sm:px-4 py-3">
                        {item?.selectedMaterial || "-"}
                      </td>
                      <td className="px-2 sm:px-4 py-3">
                        {item?.userRejected
                          ? "Rejected (Neither)"
                          : item?.selectedMaterial
                            ? "Accepted"
                            : "-"}
                      </td>
                      <td className="px-2 sm:px-4 py-3">
                        {item?.aiKeywords && item.aiKeywords.length > 0 ? (
                          <div className="flex flex-wrap gap-1">
                            {item.aiKeywords.map((keyword, index) => (
                              <span
                                key={`${item?._id}-${keyword?.label}-${index}`}
                                className="px-2 py-0.5 text-xs rounded bg-gray-100 text-gray-800"
                              >
                                {keyword?.label} ({keyword?.value})
                              </span>
                            ))}
                          </div>
                        ) : (
                          <span className="text-xs text-gray-500">-</span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {hasNextPage ? (
        <div className="flex flex-row w-full items-center justify-end mt-4">
          <PrimaryButton
            onClick={loadMore}
            label="Load more"
            className="h-10"
          />
        </div>
      ) : null}
    </div>
  );
}

export default withProtectedRoute(MaterialFeedbackScreen);
